/**
 * M3-2/M3-5 + AIW-6 + MACHINE-UX — Machine Level 1 detail (§5.2 facets).
 * Flat plane (2026-08-13): one chrome (back · id · facets · register) — no stacked
 * header + button row + second list CTA.
 * Overview = identity/presence/group only. Hardware owns board.
 * Mount: /machine/:id[/:facet]
 * Honesty: device runtime telemetry · artifact_id with activate:true
 */
import { useState } from "react";
import { html, Alert, go, hashSearchParams } from "./ui.js";
import { useApi } from "./api.js";
import { edgeSkillCatalogStatusPath, edgeSkillCatalogStatusViewOf } from "./machine-edge-skills-catalog.js";
import { edgeSkillRuntimeStatusPath, edgeSkillRuntimeStatusViewOf } from "./machine-edge-skills-runtime.js";
import { MachineHardwarePanel } from "./machine-hardware-panel.js";
import { MachineOverviewPanel } from "./machine-overview-panel.js";
import { MachineDiagnosticsPanel } from "./machine-diagnostics-panel.js";
import MachineTopologyWorkspace from "./machine-topology-workspace.js";
import { EdgeSkillsWarehouseDesk, skillsSeatOf } from "./edge-skills-warehouse.js";
import { OpsCamerasDesk } from "./ops-cameras-desk.js";
import { confirmLeaveSeat } from "./shell-wayfind.js";
import { CAP_HOME } from "./cmc-entry-registry.js";

const FACETS = [
  { id: "overview", label: "Overview" },
  { id: "hardware", label: "Hardware" },
  { id: "diagnostics", label: "Diagnostics" },
  { id: "topology", label: "Topology" },
  { id: "skills", label: "Skills" },
  { id: "cameras", label: "Cameras" },
  { id: "access", label: "Access" },
  { id: "activity", label: "Activity" },
  { id: "ai", label: "AI" },
];

function facetPath(did, facet, returnTo = "") {
  const base = "/machine/" + encodeURIComponent(did);
  const path = !facet || facet === "overview" ? base : base + "/" + facet;
  return returnTo ? path + "?return_to=" + encodeURIComponent(returnTo) : path;
}

function MachineSkillsWorkspace({ deviceId, toast }) {
  const status = useApi("/ota/status");
  const catalogStatusApi = useApi(edgeSkillCatalogStatusPath());
  const catalogStatus = edgeSkillCatalogStatusViewOf(catalogStatusApi.data);
  const runtimeStatusApi = useApi(edgeSkillRuntimeStatusPath());
  const runtimeStatus = edgeSkillRuntimeStatusViewOf(runtimeStatusApi.data);
  const seat = skillsSeatOf(status.data);
  return html`<section data-cmc-machine-workspace="skills">
    <${Alert} tone="info" title="Skills on this board"
      say="What is running comes from the board. The catalog is only a shelf. Install waits for the board to confirm."/>
    ${!runtimeStatus.productEnabled
      ? html`<${Alert} tone="warn" title="Skill changes are closed"
          say=${(runtimeStatus.detail || "This board is not ready to install skills yet") + " · install stays closed until the board is ready."}/>`
      : catalogStatus.unavailable
      ? html`<${Alert} tone="warn" title="Skill shelf is closed"
          say=${(catalogStatus.detail || "The skill shelf is not open") + " · look only."}/>`
      : html`<${Alert} tone="info" title="Skills follow the board"
          say="A click is not enough. The board must confirm before a skill is on."/>`}
    <${EdgeSkillsWarehouseDesk} mode="device" deviceId=${deviceId} toast=${toast} seat=${seat}/>
  </section>`;
}

export default function MachineDetail({ id, facet, toast }) {
  const did = String(id || "").trim();
  const at = FACETS.some((f) => f.id === facet) ? facet : "overview";
  const [aiArmed, setAiArmed] = useState(false);
  const requestedReturn = String(hashSearchParams().get("return_to") || "");
  const returnTo = requestedReturn.startsWith("/machine") ? requestedReturn : "/machine";
  const here = facetPath(did, at, returnTo);

  if (!did) {
    return html`<div className="sheet machine-detail desk-craft" data-cmc-m3="m3-2" data-cmc-shell="machine-ux"
        data-cmc-machine-flat="1">
      <${Alert} tone="warn" title="No machine selected"
        say="Open the Machine list and pick a managed device. Empty stays empty."/>
      <button type="button" className="btn btn--go btn--sm" onClick=${() => go("/machine")}>
        Machine list</button>
    </div>`;
  }

  const goFacet = (f) => go(facetPath(did, f, returnTo));
  const leave = (target, objectLabel) =>
    confirmLeaveSeat({ fromRoute: here, targetPath: target, objectLabel: objectLabel || did });

  return html`<div className="sheet machine-detail desk-craft" data-cmc-m3="m3-2" data-cmc-m35="m3-5"
      data-cmc-aiw=${at === "ai" ? "aiw-6" : ""} data-cmc-shell="machine-ux"
      data-cmc-machine-flat="1"
      data-device=${did} data-facet=${at}>
    <div className="wrap machine-detail__plane">
      <header className="machine-detail__chrome" aria-label="Machine detail">
        <button type="button" className="machine-detail__back"
          onClick=${() => go(returnTo)}>← List</button>
        <div className="machine-detail__who">
          <h1 className="machine-detail__title">${did}</h1>
          <span className="machine-detail__facet-mark">${FACETS.find((f) => f.id === at)?.label || "Detail"}</span>
        </div>
        <nav className="machine-detail__facets" aria-label="Machine facets" data-cmc-m35="m3-5">
          ${FACETS.map((f) => html`
            <button key=${f.id} type="button" role="tab" className="machine-detail__facet"
              data-on=${at === f.id ? "1" : "0"} aria-selected=${at === f.id ? "true" : "false"}
              onClick=${() => goFacet(f.id)}>${f.label}</button>`)}
        </nav>
        <div className="machine-detail__side">
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/machine/groups")}>Groups</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => confirmLeaveSeat({
              fromRoute: here,
              targetPath: "/skills/assign?device=" + encodeURIComponent(did),
            })}>Skills</button>
          <button type="button" className="btn btn--go btn--sm"
            onClick=${() => leave("/activate", "Register device")}>Activate</button>
        </div>
      </header>

      <div className="machine-detail__body">
        ${at === "overview" ? html`<${MachineOverviewPanel} deviceId=${did} toast=${toast}/>` : null}

        ${at === "hardware" ? html`<${MachineHardwarePanel} deviceId=${did} toast=${toast}/>` : null}

        ${at === "diagnostics" ? html`<${MachineDiagnosticsPanel}
          deviceId=${did} toast=${toast} fromRoute=${here}/>` : null}

        ${at === "topology" ? html`<${MachineTopologyWorkspace} deviceId=${did}/>` : null}

        ${at === "skills" ? html`<${MachineSkillsWorkspace} deviceId=${did} toast=${toast}/>` : null}

        ${at === "cameras" ? html`<section data-cmc-machine-workspace="cameras">
          <${Alert} tone="info" title="Camera ownership & binding"
            say="The canonical camera registry is embedded here for this machine; it is not a second registry."/>
          <${OpsCamerasDesk} toast=${toast} deviceId=${did}/>
        </section>` : null}

        ${at === "access" ? html`<div data-cmc-m35-facet="access" data-cmc-shell="machine-ux">
          <${Alert} tone="info" title="Access · custody & grants"
            say="Custody and shares live on Platform and Settings desks. This facet only hands you there — it does not invent grants."/>
          <div className="btn-row" style=${{ flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            <button type="button" className="btn btn--go btn--sm"
              onClick=${() => leave("/sa/custody", did)}>Custody</button>
            <button type="button" className="btn btn--ghost btn--sm"
              onClick=${() => leave("/settings/delegations", did)}>Shares</button>
            <button type="button" className="btn btn--ghost btn--sm"
              onClick=${() => leave("/settings/ai", "AI policy")}>AI policy</button>
            <button type="button" className="btn btn--ghost btn--sm" onClick=${() => goFacet("ai")}>
              AI action points</button>
          </div>
        </div>` : null}

        ${at === "activity" ? html`<div data-cmc-m35-facet="activity" data-cmc-shell="machine-ux"
            data-cmc-soft-theatre="forbidden">
          <${Alert} tone="warn" title="Activity · audit projection"
            say="History paints only from real audit rows. Empty means nothing recorded yet — not a made-up timeline."/>
          <div className="btn-row" style=${{ flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            <button type="button" className="btn btn--go btn--sm"
              onClick=${() => leave("/settings/audit", did)}>Audit export</button>
            <button type="button" className="btn btn--ghost btn--sm"
              onClick=${() => leave(CAP_HOME.needs_you.path + "?device=" + encodeURIComponent(did), did)}>
              Mission Room</button>
          </div>
        </div>` : null}

        ${at === "ai" ? html`<div data-cmc-aiw="aiw-6" data-cmc-m35-facet="ai" data-cmc-shell="machine-ux">
          <${Alert} tone="info" title="Machine AI action points"
            say="AI may propose diagnose, capability, or Mission Room handoffs. It will not invent reboot, siren, or device commands from this seat."/>
          <div className="btn-row" style=${{ flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            <button type="button" className="btn btn--ghost btn--sm"
              data-on=${aiArmed ? "1" : "0"}
              onClick=${() => setAiArmed((v) => !v)}>
              ${aiArmed ? "Confirm armed" : "Arm confirm"}</button>
            <button type="button" className="btn btn--go btn--sm" disabled=${!aiArmed}
              onClick=${() => {
                toast && toast("Opening Mission Room with this machine", "go");
                leave("/response/cases?device=" + encodeURIComponent(did), did);
              }}>
              Propose in Mission Room</button>
            <button type="button" className="btn btn--ghost btn--sm" disabled=${!aiArmed}
              onClick=${() => {
                toast && toast("Opening Diagnostics", "go");
                goFacet("diagnostics");
              }}>
              Propose Diagnostics</button>
            <button type="button" className="btn btn--ghost btn--sm" disabled=${!aiArmed}
              onClick=${() => {
                toast && toast("Opening Scoped Cap", "go");
                confirmLeaveSeat({
                  fromRoute: here,
                  targetPath: "/skills/assign?device=" + encodeURIComponent(did),
                });
              }}>
              Propose Scoped Cap</button>
          </div>
          <${Alert} tone="warn" title="Commands stay on their desks"
            say="Reboot, siren, and Cap publish stay on their own desks — only after you confirm."/>
        </div>` : null}
      </div>
    </div>
  </div>`;
}
